import { memo, useMemo, type ReactNode } from 'react';
import { getVisibleTextFromInlineTerms, parseInlineTerms } from '../utils/inline-terms';
import { TermHighlight } from './term-highlight';

interface FormattedDialogueTextProps {
  text: string;
  visibleLength: number;
}

export function getDialogueVisibleText(text: string): string {
  return getVisibleTextFromInlineTerms(text);
}

export const FormattedDialogueText = memo(({ text, visibleLength }: FormattedDialogueTextProps) => {
  const segments = useMemo(() => parseInlineTerms(text), [text]);
  const nodes = useMemo(() => {
    let remaining = visibleLength;
    const rendered: ReactNode[] = [];
    let termIndex = 0;

    for (const segment of segments) {
      if (remaining <= 0) break;

      if (segment.type === 'plain') {
        const slice = segment.text.slice(0, remaining);
        if (slice) rendered.push(slice);
        remaining -= slice.length;
        continue;
      }

      const termLength = segment.term.length;
      if (remaining >= termLength) {
        const index = termIndex++;
        rendered.push(
          <TermHighlight
            key={`term-${index}`}
            term={segment.term}
            definition={segment.definition}
          />
        );
        remaining -= termLength;
      } else {
        rendered.push(segment.term.slice(0, remaining));
        remaining = 0;
      }
    }

    return rendered;
  }, [segments, visibleLength]);

  return <>{nodes}</>;
});

FormattedDialogueText.displayName = 'FormattedDialogueText';
